import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const faqs = [
  {
    q: "What is jurisdictional strategy?",
    a: "Jurisdictional strategy is the deliberate architecture of where you live for tax purposes, where you hold citizenship, where your capital is domiciled, and how these layers interact over time. It treats geography as a strategic variable, not a given."
  },
  {
    q: "How does this differ from immigration consulting?",
    a: "Immigration consulting typically starts with a destination and works backward. Jurisdictional strategy starts with your full picture: tax exposure, asset structure, operating entities, family considerations, and long-term objectives. The destination, if there is one, emerges from the analysis."
  },
  {
    q: "What is The Strategic Discovery Session?",
    a: "The Strategic Discovery Session is our diagnostic framework. It covers seven dimensions of a client's cross-border life: tax residency, citizenship portfolio, banking and custody, asset domicile, operating company headquarters, personal mobility infrastructure, and generational succession. Most clients have never had all seven reviewed together."
  },
  {
    q: "Who are your typical clients?",
    a: "Founders preparing for or following a liquidity event, senior executives with cross border exposure their existing advisors do not cover, and family offices managing geographic risk across generations."
  },
  {
    q: "Do you represent a particular country or program?",
    a: "No. We are independent. We do not have a preferred jurisdiction, and we do not receive commissions from any program or government. Our advice is structurally unconflicted."
  },
  {
    q: "What does an initial engagement look like?",
    a: "Every engagement begins with The Strategic Discovery Session: a confidential, two to three hour working conversation that maps your current position across all seven dimensions and identifies gaps, risks, and opportunities. From there, we deliver a written strategic recommendation."
  },
  {
    q: "How long does a typical engagement take?",
    a: "The Strategic Discovery Session is typically completed within four to six weeks. Implementation timelines depend on the complexity of the strategy: some elements move in months, others are staged over years."
  },
  {
    q: "How do I begin a conversation?",
    a: "By introduction. Use the contact form on this site or reach us at contact@globalcitizenconsult.com. Initial conversations are confidential and do not constitute an engagement. Most introductions are answered within two business days."
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-20 md:py-28 bg-[#f7f6f3]"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-4xl">
        <div className="text-center mb-14">
          <p
            className="text-sm uppercase tracking-[0.3em] text-[#1a7a7a] mb-3 font-medium"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Common Questions
          </p>
          <h2
            className="text-4xl md:text-5xl font-semibold tracking-tight text-[#1a1a1a]"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Frequently Asked Questions
          </h2>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div
              key={i}
              data-testid={`faq-item-${i}`}
              className={`rounded-xl border transition-all duration-300 ${
                openIndex === i
                  ? 'border-[#1a7a7a]/30 bg-white shadow-sm'
                  : 'border-gray-200 bg-white/60 hover:border-[#1a7a7a]/20'
              }`}
            >
              <button
                onClick={() => toggle(i)}
                data-testid={`faq-toggle-${i}`}
                className="w-full flex items-center justify-between px-6 py-5 text-left"
              >
                <span
                  className="text-[#1a2332] font-medium text-base pr-4"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {faq.q}
                </span>
                <ChevronDown
                  size={20}
                  className={`text-[#1a7a7a] flex-shrink-0 transition-transform duration-300 ${
                    openIndex === i ? 'rotate-180' : ''
                  }`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === i ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <p
                  className="px-6 pb-5 text-[#5a6370] text-sm leading-relaxed"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {faq.a}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
